import { Alert, Box, Button, Checkbox, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Typography } from "@mui/material";
import ExcelJS from "exceljs";
import React from "react";
import { NotFoundSection, SectionPaper } from "../../../components";
import { useAppDispatch } from "../../../redux";
import { closeConfirmationDialog, openConfirmationDialog } from "../../../redux/slices/confirmationDialog";
import { closeDialog } from "../../../redux/slices/dialog";
import * as XLSX from "xlsx";

interface ExcelRow {
    [key: string]: string | number | undefined | ExcelJS.CellValue;
}

export interface ExcelImportState { name: string, data: ExcelRow[] }

export const ExcelImporter: React.FC<{ onDataOk: (data: ExcelImportState[]) => void, tableLimit: number, currentCategoryLimit: number }> = ({ onDataOk, tableLimit, currentCategoryLimit }) => {

    const dispatch = useAppDispatch();

    const [data, setData] = React.useState<ExcelImportState[]>([]);
    const [fileName, setFileName] = React.useState<string>("");
    const [selectedSheet, setSelectedSheet] = React.useState<number[]>([]);
    const [tab, setTab] = React.useState<number>(0);
    const fileInputRef = React.useRef<HTMLInputElement | null>(null);

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setFileName(file.name);

        const reader = new FileReader();

        reader.onload = (e) => {
            if (!e?.target?.result) return;
            const data = new Uint8Array(e?.target?.result as ArrayBuffer);
            const workbook = XLSX.read(data, { type: "array" });

            const newData: any = [];

            workbook.SheetNames.forEach((sheetName) => {
                const worksheet = workbook.Sheets[sheetName];

                // Convert sheet to 2D array
                let sheetData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                // Remove trailing empty rows
                sheetData = sheetData.filter((row: any) =>
                    row?.some((cell: any) => cell !== null && cell !== undefined && cell !== "")
                );

                // Detect header row dynamically
                let headerRowIndex = -1;
                let headers: any[] = [];
                for (let i = 0; i < sheetData.length; i++) {
                    const row = sheetData[i] as any[];
                    const meaningfulCells = row?.filter((cell: any) => cell); // Non-empty cells
                    if (meaningfulCells.length > 1) {
                        headerRowIndex = i;
                        headers = row?.slice(meaningfulCells.findIndex((cell: any) => cell !== "")); // Detect starting column
                        break;
                    }
                }

                if (headerRowIndex === -1) {
                    console.warn(`No valid headers detected in sheet: ${sheetName}`);
                    return;
                }

                // Extract rows below the header
                const rows = sheetData.slice(headerRowIndex + 1).map((row: any) => {
                    const rowData: Record<string, any> = {};
                    headers.forEach((header, colIndex) => {
                        const value = row[colIndex + headers.findIndex((cell) => cell !== "")] || ""; // Adjust index
                        rowData[header] = value !== null && value !== undefined ? value.toString() : ""; // Convert to string
                    });
                    return rowData;
                });

                newData.push({ name: sheetName, data: rows });
            });

            setData(newData);
            setSelectedSheet(Object.keys(newData).map((_, index) => index));

        };

        reader.readAsArrayBuffer(file);
    };

    const reset = () => {
        setData([]);
        setFileName("");
        if (fileInputRef.current) {
            fileInputRef.current.value = ""; // Reset the file input value
        }
    };

    const updateSelectedSheet = (index: number, checked: boolean) => {
        if (checked) {
            setSelectedSheet([...selectedSheet, index])
        } else {
            selectedSheet.splice(selectedSheet.indexOf(index), 1);
            setSelectedSheet([...selectedSheet]);
        }
    };

    const close = () => {
        dispatch(closeDialog());
    };

    const onImportBtnClicked = () => {
        dispatch(openConfirmationDialog({
            title: "Are you sure?",
            body: <Stack>
                <Typography>{`Do you want to import the below sheets as table in the template?`}</Typography>
                <ul>
                    {data.filter((_, index) => selectedSheet.includes(index)).map(_data => (<li>{_data.name}</li>))}
                </ul>

                {(currentCategoryLimit + selectedSheet.length) > tableLimit && <Typography variant="body2" color={"error"}>{`Error: You are having ${currentCategoryLimit} tables, you can only have up to ${tableLimit} tables. Adding more (${selectedSheet.length}) isn't allowed.`}</Typography>}
            </Stack>,
            onOk: () => onDataOk(data.filter((_, index) => selectedSheet.includes(index))),
            onNegativeBtn: () => dispatch(closeConfirmationDialog())
        }))
    };

    return (
        <Box
            sx={{
                pb: 2,
                height: "80vh",
                overflow: "auto",
                position: "relative"
            }}>
            <SectionPaper
                title="Import from Excel File"
                hideExpandCollapse={true}
                removeShadow={true}
                renderButtons={[
                    data.length > 0 ? <Button variant="outlined" onClick={reset}>
                        Clear File
                    </Button> : <></>,
                    <Button variant="contained" component="label">
                        Upload Excel File
                        <input ref={fileInputRef} type="file" accept=".xlsx, .xls" hidden onChange={handleFileUpload} />
                    </Button>
                ]}>
                <Box sx={{
                    pb: 6
                }}>
                    {data.length > 0 && <Stack spacing={2}>
                        <Typography variant="body1" sx={{ fontWeight: "bold" }} >{`${fileName}`}</Typography>

                        <Box>
                            <Tabs
                                value={tab}
                                onChange={(e, tab) => setTab(tab)}
                                indicatorColor="primary"
                                textColor="primary"
                                sx={{
                                    background: (theme) => theme.palette.background.default,
                                    border: (theme) => `1px solid ${theme.palette.divider}`,
                                    borderTopLeftRadius: 8, borderTopRightRadius: 8
                                }}
                            >
                                {data.map((_tab, _index) => {
                                    return (
                                        <Tab
                                            id={_tab.name}
                                            sx={{ background: tab === _index ? "white" : "" }}
                                            value={_index}
                                            label={<Stack
                                                direction={"row"}
                                                sx={{
                                                    alignItems: "center",
                                                    justifyContent: "center"
                                                }}>
                                                <Typography>{_tab.name}</Typography>
                                                <Checkbox checked={selectedSheet.includes(_index)} onChange={(e, checked) => updateSelectedSheet(_index, checked)} />
                                            </Stack>}
                                            key={_index}
                                        />
                                    );
                                })}
                            </Tabs>

                            <Box>
                                <TableContainer sx={{ border: (theme) => `1px solid ${theme.palette.divider}`, borderBottomLeftRadius: 8, borderBottomRightRadius: 8 }}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                {Object.keys(data[tab].data[0] ?? {}).map((key, index) => (
                                                    <TableCell key={index} sx={{ fontWeight: "bold" }}>{key}</TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {data[tab].data.map((row, rowIndex) => (
                                                <TableRow key={rowIndex}>
                                                    {Object.values(row).map((value, cellIndex) => (
                                                        <TableCell key={cellIndex}>{value?.toString()}</TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        </Box>

                    </Stack>}

                {data.length === 0 && (
                    <Stack alignItems={"center"}>
                        <Stack 
                            gap={1} 
                            sx={{ maxWidth: "420px", justifyContent:"center", alignItems:"center" }}
                        >
                            <NotFoundSection message="Click 'UPLOAD EXCEL FILE' button to upload excel file" />
                            <Alert color="info">The first row will be treated as the header, while the subsequent rows will be processed as data during import.</Alert>
                        </Stack>
                    </Stack>
                )}
            </Box>
        </SectionPaper>

            <Stack
                spacing={1}
                direction={"row"}
                sx={{
                    mt: 2,
                    p: 2,
                    justifyContent: "flex-end",
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    width: "100%",
                    backgroundColor: (theme) => theme.palette.background.paper,
                    boxShadow: `rgba(0, 0, 0, 0.06) 0px 2px 4px 0px inset`
                }}>
                <Button variant="outlined" onClick={close}>Cancel</Button>
                {data.length > 0 && <Button variant="contained" onClick={onImportBtnClicked}>Import</Button>}
            </Stack>
        </Box>
    );
}
